<script>
  import { onMount } from 'svelte';
  import { workspace, callBbAi, refreshWorkspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';

  let { id, onClose } = $props();

  let step = $state(null);
  let loading = $state(true);
  let saving = $state(false);
  let error = $state(null);

  let pushbackField = $state(null);
  let pushbackResult = $state(null);
  let pushbackLoading = $state(false);

  let auditResult = $state(null);
  let auditLoading = $state(false);

  let discarding = $state(false);

  // Local working state for tags as comma-separated text
  let tagsStr = $state('');

  // v2.18e — branching state
  let processes = $state([]);
  let processesDirty = $state(false);
  let showAddNext = $state(false);
  let newNextSegmentKey = $state('');

  const children = $derived(workspace.steps.filter(s => s.parent_step_id === id && !s.deleted_at));

  const outgoingConnections = $derived.by(() => {
    return (workspace.connections || [])
      .filter(c => c.from_segment_id === id && c.from_segment_kind === 'step')
      .map(c => {
        const target =
          c.to_segment_kind === 'step' ? workspace.steps.find(s => s.id === c.to_segment_id) :
          c.to_segment_kind === 'deal' ? workspace.deals.find(d => d.id === c.to_segment_id) :
          c.to_segment_kind === 'audience' ? workspace.audiences.find(a => a.id === c.to_segment_id) : null;
        return {
          id: c.id,
          targetKind: c.to_segment_kind,
          targetName: target?.step_name || target?.name || '(unknown)',
          chain_id: c.chain_id
        };
      });
  });

  function uuid() { return crypto.randomUUID(); }
  function markProcessesDirty() { processesDirty = true; }
  function addProcess() {
    processes = [...processes, { id: uuid(), label: '', order: processes.length }];
    processesDirty = true;
  }
  function removeProcess(i) {
    processes = processes.filter((_, idx) => idx !== i).map((p, idx) => ({ ...p, order: idx }));
    processesDirty = true;
  }
  function moveProcessUp(i) {
    if (i === 0) return;
    const arr = [...processes];
    [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
    processes = arr.map((p, idx) => ({ ...p, order: idx }));
    processesDirty = true;
  }
  function moveProcessDown(i) {
    if (i === processes.length - 1) return;
    const arr = [...processes];
    [arr[i], arr[i+1]] = [arr[i+1], arr[i]];
    processes = arr.map((p, idx) => ({ ...p, order: idx }));
    processesDirty = true;
  }
  async function saveProcesses() {
    saving = true;
    try {
      const cleaned = processes.filter(p => p.label.trim()).map((p, i) => ({ ...p, label: p.label.trim(), order: i }));
      const { error: e } = await supabase.from('bb_steps').update({ processes: cleaned }).eq('id', id);
      if (e) throw e;
      processes = cleaned;
      processesDirty = false;
      await refreshWorkspace();
      showToast('Processes saved', 'success');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      saving = false;
    }
  }

  async function addChild() {
    saving = true;
    try {
      const { data, error: e } = await supabase.from('bb_steps').insert({
        workspace_id: step.workspace_id,
        parent_step_id: id,
        step_name: 'New child step',
        state: 'user',
        source: 'user',
        confidence: 'low',
        perceived_cost: 5,
        perceived_value: 5,
        status: 'draft'
      }).select().single();
      if (e) throw e;
      await refreshWorkspace();
      showToast('Child step added — opening editor', 'success');
      onClose();
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-step-editor', { detail: { id: data.id } }));
      }, 100);
    } catch (e) {
      showToast('Add child failed: ' + e.message, 'error');
    } finally {
      saving = false;
    }
  }

  function openChild(childId) {
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('open-step-editor', { detail: { id: childId } }));
    }, 100);
  }

  async function removeChildStep(childId) {
    if (!confirm('Unlink this child step? It becomes a top-level step.')) return;
    try {
      const { error: e } = await supabase.from('bb_steps').update({ parent_step_id: null }).eq('id', childId);
      if (e) throw e;
      await refreshWorkspace();
      showToast('Child unlinked', 'success');
    } catch (e) {
      showToast('Unlink failed: ' + e.message, 'error');
    }
  }

  async function addNext() {
    if (!newNextSegmentKey) return;
    const [toKind, toId] = newNextSegmentKey.split(':');
    if (toKind === 'audience') {
      showToast("Audience can't come after a step", 'error');
      return;
    }
    const existingChainConn = (workspace.connections || []).find(c =>
      (c.from_segment_id === id || c.to_segment_id === id) && c.chain_id
    );
    saving = true;
    try {
      const { error: e } = await supabase.from('bb_segment_connections').insert({
        workspace_id: step.workspace_id,
        from_segment_id: id, from_segment_kind: 'step',
        to_segment_id: toId, to_segment_kind: toKind,
        chain_id: existingChainConn?.chain_id || null,
        sort_order: 0
      });
      if (e) throw e;
      await refreshWorkspace();
      showToast('Next step added', 'success');
      showAddNext = false;
      newNextSegmentKey = '';
    } catch (e) {
      showToast('Add failed: ' + e.message, 'error');
    } finally {
      saving = false;
    }
  }

  async function removeNext(connId) {
    if (!confirm('Remove this connection? The target segment stays.')) return;
    try {
      const { error: e } = await supabase.from('bb_segment_connections').delete().eq('id', connId);
      if (e) throw e;
      await refreshWorkspace();
      showToast('Connection removed', 'success');
    } catch (e) {
      showToast('Remove failed: ' + e.message, 'error');
    }
  }

  const TEXT_FIELDS = [
    { key: 'step_name', label: 'Title', type: 'input' },
    { key: 'step_description', label: 'Description', type: 'textarea' },
    { key: 'purpose', label: 'Purpose', type: 'textarea' },
    { key: 'what_user_does', label: 'What user does', type: 'textarea' },
    { key: 'what_is_delivered', label: 'What is delivered', type: 'textarea' }
  ];

  onMount(async () => {
    await loadStep();
  });

  async function loadStep() {
    loading = true;
    const { data, error: e } = await supabase.from('bb_steps').select('*').eq('id', id).maybeSingle();
    if (e || !data) {
      error = e?.message || 'Step not found';
    } else {
      step = data;
      tagsStr = (data.tags || []).join(', ');
      processes = Array.isArray(data.processes) ? [...data.processes] : [];
      processesDirty = false;
    }
    loading = false;
  }

  async function saveField(key) {
    if (!step) return;
    saving = true; error = null;
    const { error: e } = await supabase.from('bb_steps').update({ [key]: step[key], state: 'user' }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  async function saveNumeric(key) {
    if (!step) return;
    const v = step[key];
    const clean = v === null || v === undefined || v === '' ? null : Math.max(0, Math.min(10, Number(v) || 0));
    step[key] = clean;
    saving = true; error = null;
    const { error: e } = await supabase.from('bb_steps').update({ [key]: clean, state: 'user' }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  async function saveBool(key) {
    if (!step) return;
    saving = true; error = null;
    const { error: e } = await supabase.from('bb_steps').update({ [key]: step[key] }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  async function saveTags() {
    if (!step) return;
    const tags = tagsStr.split(',').map(t => t.trim()).filter(Boolean);
    step.tags = tags;
    saving = true; error = null;
    const { error: e } = await supabase.from('bb_steps').update({ tags }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  // Step type with autocomplete
  let typeFocused = $state(false);

  const knownTypes = $derived.by(() => {
    const set = new Set();
    for (const s of workspace.steps) {
      if (s.step_type && s.step_type.trim()) set.add(s.step_type.trim());
    }
    return [...set].sort();
  });

  const typeSuggestions = $derived.by(() => {
    if (!typeFocused) return [];
    const q = (step?.step_type || '').trim().toLowerCase();
    if (!q) return knownTypes.slice(0, 8);
    return knownTypes.filter(t => t.toLowerCase().includes(q) && t.toLowerCase() !== q).slice(0, 8);
  });

  async function saveStepType() {
    if (!step) return;
    const trimmed = (step.step_type || '').trim();
    step.step_type = trimmed || null;
    saving = true; error = null;
    const { error: e } = await supabase.from('bb_steps').update({ step_type: step.step_type }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  function pickType(t) {
    if (!step) return;
    step.step_type = t;
    typeFocused = false;
    saveStepType();
  }

  async function runPushback(fieldKey) {
    pushbackField = fieldKey;
    pushbackResult = null;
    pushbackLoading = true;
    try {
      const result = await callBbAi('pushback_field', {
        entity_kind: 'step',
        entity_data: step,
        field_name: fieldKey,
        current_value: step[fieldKey] ?? ''
      });
      pushbackResult = result;
    } catch (e) {
      pushbackResult = { critique: 'Pushback failed: ' + e.message };
    } finally {
      pushbackLoading = false;
    }
  }

  function applyPushback() {
    if (pushbackResult?.replacement && pushbackField) {
      step[pushbackField] = pushbackResult.replacement;
      saveField(pushbackField);
    }
    pushbackResult = null;
    pushbackField = null;
  }

  function dismissPushback() {
    pushbackResult = null;
    pushbackField = null;
  }

  async function runAudit() {
    auditResult = null;
    auditLoading = true;
    try {
      const result = await callBbAi('audit', { scope: 'step', scope_id: id });
      auditResult = result;
    } catch (e) {
      auditResult = { summary: 'Audit failed: ' + e.message, findings: [] };
    } finally {
      auditLoading = false;
    }
  }

  async function discardStep() {
    if (!confirm('Discard this step? You can recover it from the database within 30 days.')) return;
    discarding = true;
    try {
      const { error: e } = await supabase.from('bb_steps').update({ deleted_at: new Date().toISOString() }).eq('id', id);
      if (e) throw e;
      await refreshWorkspace();
      showToast('Step discarded', 'success');
      onClose();
    } catch (e) {
      showToast('Discard failed: ' + e.message, 'error');
    } finally {
      discarding = false;
    }
  }

  function fieldConfidence(key) {
    return step?.field_confidence?.[key];
  }
</script>

<div class="ee-overlay" onclick={onClose}>
  <div class="ee-modal" onclick={(e) => e.stopPropagation()}>
    <div class="ee-header">
      <div>
        <div class="ee-kind-label">Step</div>
        <h3>{step?.step_name || (loading ? 'Loading…' : 'Untitled')}</h3>
      </div>
      <button class="ee-close" onclick={onClose} type="button">×</button>
    </div>

    {#if loading}
      <div class="ee-loading">Loading…</div>
    {:else if error}
      <div class="ee-error">{error}</div>
    {:else if step}
      <div class="ee-actions-row">
        <button class="ee-action" onclick={runAudit} disabled={auditLoading} type="button">
          {auditLoading ? 'Auditing…' : '🔎 Audit'}
        </button>
        <button class="ee-action ee-discard" onclick={discardStep} disabled={discarding} type="button">
          {discarding ? 'Discarding…' : '🗑 Discard'}
        </button>
      </div>

      {#if auditResult}
        <div class="ee-audit-card">
          <div class="ee-audit-summary">{auditResult.summary}</div>
          {#if auditResult.findings?.length}
            <ul class="ee-findings">
              {#each auditResult.findings as f}
                <li class="ee-finding ee-{f.severity}">
                  <span class="ee-sev">{f.severity}</span>
                  <div>
                    <div class="ee-finding-text"><strong>{f.area}:</strong> {f.finding}</div>
                    {#if f.suggestion}<div class="ee-suggest">{f.suggestion}</div>{/if}
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
          <button class="ee-secondary" onclick={() => auditResult = null} type="button">Close audit</button>
        </div>
      {/if}

      <div class="ee-fields">
        <div class="ee-field se-field-type">
          <label>
            Type
            <span class="se-label-hint">— e.g. Discovery, Onboarding, Payment</span>
          </label>
          <div class="se-type-wrap">
            <input
              type="text"
              bind:value={step.step_type}
              onfocus={() => typeFocused = true}
              onblur={() => { setTimeout(() => typeFocused = false, 150); saveStepType(); }}
              placeholder="What kind of step is this?"
            />
            {#if typeFocused && typeSuggestions.length > 0}
              <div class="se-type-suggestions">
                {#each typeSuggestions as t}
                  <button class="se-type-suggestion" onmousedown={() => pickType(t)} type="button">{t}</button>
                {/each}
              </div>
            {/if}
          </div>
        </div>

        {#each TEXT_FIELDS as f}
          <div class="ee-field">
            <div class="ee-field-head">
              <label>
                {f.label}
                {#if fieldConfidence(f.key)}
                  <span class="ee-conf ee-conf-{fieldConfidence(f.key)}">{fieldConfidence(f.key)}</span>
                {/if}
              </label>
              <button class="ee-pushback-btn" onclick={() => runPushback(f.key)} disabled={pushbackLoading && pushbackField === f.key} type="button">
                {pushbackLoading && pushbackField === f.key ? '…' : '↻ Push back'}
              </button>
            </div>
            {#if f.type === 'textarea'}
              <textarea bind:value={step[f.key]} rows="2" onblur={() => saveField(f.key)}></textarea>
            {:else}
              <input bind:value={step[f.key]} onblur={() => saveField(f.key)} />
            {/if}

            {#if pushbackField === f.key && pushbackResult}
              <div class="ee-pushback-card">
                <div class="ee-pb-critique">{pushbackResult.critique}</div>
                {#if pushbackResult.replacement}
                  <div class="ee-pb-replacement">
                    <div class="ee-pb-label">Drop-in replacement:</div>
                    <div class="ee-pb-text">{pushbackResult.replacement}</div>
                  </div>
                  <div class="ee-pb-actions">
                    <button class="ee-primary" onclick={applyPushback} type="button">Apply</button>
                    <button class="ee-secondary" onclick={dismissPushback} type="button">Dismiss</button>
                  </div>
                {:else if pushbackResult.questions?.length}
                  <ul class="ee-pb-questions">
                    {#each pushbackResult.questions as q}
                      <li>{q}</li>
                    {/each}
                  </ul>
                  <button class="ee-secondary" onclick={dismissPushback} type="button">Got it</button>
                {:else}
                  <button class="ee-secondary" onclick={dismissPushback} type="button">Dismiss</button>
                {/if}
              </div>
            {/if}
          </div>
        {/each}

        <!-- Cost / Value sliders -->
        <div class="ee-field">
          <label>Perceived cost (0–10)</label>
          <div class="se-slider-row">
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              bind:value={step.perceived_cost}
              onchange={() => saveNumeric('perceived_cost')}
            />
            <input
              class="se-num"
              type="number"
              min="0"
              max="10"
              bind:value={step.perceived_cost}
              onblur={() => saveNumeric('perceived_cost')}
            />
          </div>
        </div>

        <div class="ee-field">
          <label>Perceived value (0–10)</label>
          <div class="se-slider-row">
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              bind:value={step.perceived_value}
              onchange={() => saveNumeric('perceived_value')}
            />
            <input
              class="se-num"
              type="number"
              min="0"
              max="10"
              bind:value={step.perceived_value}
              onblur={() => saveNumeric('perceived_value')}
            />
          </div>
          {#if (step.perceived_cost ?? 0) > (step.perceived_value ?? 0)}
            <div class="se-warn">⚠ Cost exceeds value — likely leak point</div>
          {/if}
        </div>

        <!-- Confidence -->
        <div class="ee-field">
          <label>AI confidence</label>
          <select bind:value={step.confidence} onchange={() => saveField('confidence')}>
            <option value="">—</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
            <option value="low">low</option>
          </select>
        </div>

        <!-- needs_help -->
        <div class="ee-field">
          <label class="se-check">
            <input type="checkbox" bind:checked={step.needs_help} onchange={() => saveBool('needs_help')} />
            <span>Needs help</span>
          </label>
          {#if step.needs_help}
            <input
              class="se-helper-role"
              bind:value={step.helper_role}
              placeholder="Who? (e.g. developer, designer, salesperson, yourself)"
              onblur={() => saveField('helper_role')}
            />
          {/if}
        </div>

        <!-- is_first_value -->
        <div class="ee-field">
          <label class="se-check">
            <input type="checkbox" bind:checked={step.is_first_value} onchange={() => saveBool('is_first_value')} />
            <span>First value moment</span>
          </label>
        </div>

        <!-- Tags -->
        <div class="ee-field">
          <label>Tags</label>
          <input
            bind:value={tagsStr}
            placeholder="comma-separated (e.g. onboarding, consultation)"
            onblur={saveTags}
          />
        </div>

        <!-- Children -->
        <section class="se-section">
          <h3>Children</h3>
          {#each children as child (child.id)}
            <div class="se-child-row">
              <button class="se-child-name" onclick={() => openChild(child.id)} type="button">{child.step_name || 'Untitled'}</button>
              <button class="se-row-discard" onclick={() => removeChildStep(child.id)} type="button" title="Remove child link">✕</button>
            </div>
          {/each}
          <button class="se-add-row" onclick={addChild} disabled={saving} type="button">+ Add child step</button>
        </section>

        <!-- Processes -->
        <section class="se-section">
          <h3>Processes <span class="se-section-hint">— sub-tasks within this step</span></h3>
          {#if processes.length === 0}
            <div class="se-empty-row">No process items yet</div>
          {/if}
          {#each processes as p, i (p.id)}
            <div class="se-process-row">
              <div class="se-process-controls">
                <button onclick={() => moveProcessUp(i)} disabled={i === 0} type="button" title="Up">↑</button>
                <button onclick={() => moveProcessDown(i)} disabled={i === processes.length - 1} type="button" title="Down">↓</button>
              </div>
              <input class="se-process-label" bind:value={p.label} oninput={markProcessesDirty} placeholder="Process step" />
              <button class="se-row-discard" onclick={() => removeProcess(i)} type="button" title="Remove">✕</button>
            </div>
          {/each}
          <div class="se-process-actions">
            <button class="se-add-row" onclick={addProcess} type="button">+ Add process item</button>
            {#if processesDirty}
              <button class="se-save-processes" onclick={saveProcesses} disabled={saving} type="button">{saving ? 'Saving…' : 'Save processes'}</button>
            {/if}
          </div>
        </section>

        <!-- Next steps -->
        <section class="se-section">
          <h3>Next steps <span class="se-section-hint">— what comes after this</span></h3>
          {#each outgoingConnections as cn (cn.id)}
            <div class="se-next-row">
              <span class="se-next-arrow">→</span>
              <span class="se-next-kind">{cn.targetKind}</span>
              <span class="se-next-name">{cn.targetName}</span>
              <button class="se-row-discard" onclick={() => removeNext(cn.id)} type="button" title="Remove connection">✕</button>
            </div>
          {/each}
          {#if !showAddNext}
            <button class="se-add-row" onclick={() => showAddNext = true} type="button">+ Add next</button>
          {:else}
            <div class="se-add-next">
              <select bind:value={newNextSegmentKey}>
                <option value="">— Pick a segment —</option>
                <optgroup label="Steps">
                  {#each workspace.steps.filter(s => s.id !== id && !s.deleted_at) as s}
                    <option value="step:{s.id}">{s.step_name || 'Untitled step'}</option>
                  {/each}
                </optgroup>
                <optgroup label="Offers">
                  {#each workspace.deals.filter(d => !d.deleted_at) as d}
                    <option value="deal:{d.id}">{d.name}</option>
                  {/each}
                </optgroup>
              </select>
              <button class="se-confirm-next" onclick={addNext} disabled={!newNextSegmentKey || saving} type="button">{saving ? 'Adding…' : 'Add'}</button>
              <button class="se-cancel-next" onclick={() => { showAddNext = false; newNextSegmentKey = ''; }} type="button">Cancel</button>
            </div>
          {/if}
        </section>
      </div>
    {/if}
  </div>
</div>

<style>
  .ee-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 900;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .ee-modal {
    background: linear-gradient(180deg, rgba(20,18,22,0.96), rgba(12,11,14,0.96));
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 18px;
    width: 100%;
    max-width: 720px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05);
    animation: modalIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .ee-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .ee-kind-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: #d4af37;
    margin-bottom: 4px;
  }
  .ee-header h3 {
    font-family: 'DM Serif Display', serif;
    font-size: 24px;
    font-weight: 400;
    margin: 0;
    color: #f0efe9;
    letter-spacing: -0.01em;
  }
  .ee-close {
    background: transparent; border: none;
    color: #888; font-size: 24px; cursor: pointer;
    padding: 0 8px; line-height: 1;
    margin: 0;
  }
  .ee-close:hover { color: #fff; background: transparent; }
  .ee-loading, .ee-error {
    padding: 40px; text-align: center; color: #888;
  }
  .ee-error { color: #d48484; }

  .ee-actions-row {
    display: flex; gap: 8px; padding: 14px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    flex-wrap: wrap;
  }
  .ee-action {
    background: rgba(212,175,55,0.1);
    border: 1px solid rgba(212,175,55,0.25);
    color: #d4af37;
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    cursor: pointer;
    margin: 0;
    box-shadow: none;
    font-weight: 500;
  }
  .ee-action:hover:not(:disabled) {
    background: rgba(212,175,55,0.2);
  }
  .ee-action:disabled { opacity: 0.4; }
  .ee-discard {
    background: rgba(220,100,100,0.1);
    color: #d48484;
    border: 1px solid rgba(220,100,100,0.25);
  }
  .ee-discard:hover:not(:disabled) {
    background: rgba(220,100,100,0.2);
  }

  .ee-audit-card {
    margin: 16px 24px;
    padding: 16px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
  }
  .ee-audit-summary {
    color: #f0efe9;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 12px;
    font-style: italic;
  }
  .ee-findings { list-style: none; padding: 0; margin: 0 0 12px; display: flex; flex-direction: column; gap: 8px; }
  .ee-finding {
    display: flex; gap: 10px; align-items: flex-start;
    padding: 10px 12px;
    background: rgba(255,255,255,0.02);
    border-left: 3px solid;
    border-radius: 6px;
  }
  .ee-red { border-left-color: #d48484; }
  .ee-amber { border-left-color: #d4af37; }
  .ee-green { border-left-color: #7cd992; }
  .ee-sev {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    padding: 2px 8px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .ee-red .ee-sev { background: rgba(212,100,100,0.2); color: #d48484; }
  .ee-amber .ee-sev { background: rgba(212,175,55,0.2); color: #d4af37; }
  .ee-green .ee-sev { background: rgba(124,217,146,0.2); color: #7cd992; }
  .ee-finding-text { color: #d8d8dc; font-size: 13px; line-height: 1.5; }
  .ee-finding-text strong { color: #f0efe9; }
  .ee-suggest {
    color: #9aa0a6; font-size: 12px; margin-top: 4px; font-style: italic;
  }

  .ee-fields {
    flex: 1;
    overflow-y: auto;
    padding: 16px 24px 24px;
  }
  .ee-field { margin-bottom: 18px; }
  .ee-field-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 6px;
  }
  .ee-field-head label, .ee-field > label {
    color: #d8d8dc;
    font-size: 12px;
    font-weight: 500;
    display: flex; align-items: center; gap: 8px;
  }
  .ee-conf {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    padding: 1px 6px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .ee-conf-high { background: rgba(124,217,146,0.18); color: #7cd992; }
  .ee-conf-medium { background: rgba(212,175,55,0.18); color: #d4af37; }
  .ee-conf-low { background: rgba(212,100,100,0.18); color: #d48484; }

  .ee-pushback-btn {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: #9aa0a6;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    cursor: pointer;
    margin: 0;
    box-shadow: none;
  }
  .ee-pushback-btn:hover:not(:disabled) {
    color: #d4af37;
    border-color: rgba(212,175,55,0.4);
  }
  .ee-pushback-btn:disabled { opacity: 0.4; }

  .ee-fields textarea, .ee-fields input[type="text"], .ee-fields input:not([type]), .ee-fields input[type="number"], .ee-fields select {
    width: 100%;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f4f6;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13.5px;
    box-sizing: border-box;
    font-family: inherit;
    line-height: 1.5;
    margin: 0;
  }
  .ee-fields textarea:focus, .ee-fields input:focus, .ee-fields select:focus {
    outline: none;
    border-color: rgba(212,175,55,0.4);
  }

  .ee-pushback-card {
    margin-top: 8px;
    padding: 12px 14px;
    background: rgba(212,175,55,0.06);
    border: 1px solid rgba(212,175,55,0.2);
    border-radius: 8px;
  }
  .ee-pb-critique {
    color: #f0efe9;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 10px;
    font-style: italic;
  }
  .ee-pb-replacement {
    background: rgba(0,0,0,0.3);
    padding: 10px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
  }
  .ee-pb-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #d4af37;
    margin-bottom: 4px;
  }
  .ee-pb-text {
    color: #f0efe9;
    font-size: 13px;
    line-height: 1.5;
  }
  .ee-pb-actions { display: flex; gap: 6px; }
  .ee-pb-questions {
    list-style: disc; padding-left: 20px; color: #d8d8dc; font-size: 12px;
    margin: 0 0 10px;
  }
  .ee-pb-questions li { margin-bottom: 4px; line-height: 1.5; }
  .ee-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c; border: none;
    padding: 8px 16px; border-radius: 6px; cursor: pointer;
    font-weight: 600; font-size: 12px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
    margin: 0;
  }
  .ee-secondary {
    background: rgba(255,255,255,0.04);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 8px 16px; border-radius: 6px; cursor: pointer;
    font-size: 12px;
    margin: 0; box-shadow: none;
  }

  /* StepEditor-specific */
  .se-slider-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .se-slider-row input[type="range"] {
    flex: 1;
    margin: 0;
    background: transparent;
    padding: 0;
    border: none;
    accent-color: #d4af37;
  }
  .se-slider-row .se-num {
    width: 64px;
    flex: none;
    text-align: center;
  }
  .se-warn {
    margin-top: 6px;
    color: #d48484;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.4px;
  }
  .se-check {
    cursor: pointer;
    user-select: none;
  }
  .se-check input[type="checkbox"] {
    width: auto !important;
    margin: 0 !important;
    accent-color: #d4af37;
  }
  .se-helper-role {
    margin-top: 8px;
  }

  .se-field-type { position: relative; }
  .se-label-hint {
    color: #6a6a6e;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0.4px;
    font-size: 9px;
    margin-left: 4px;
  }
  .se-type-wrap { position: relative; }
  .se-type-suggestions {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.25);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
  }
  .se-type-suggestion {
    display: block;
    width: 100%;
    background: none;
    border: none;
    color: #d8d8dc;
    padding: 7px 12px;
    text-align: left;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.1s ease;
    margin: 0;
  }
  .se-type-suggestion:hover {
    background: rgba(212,175,55,0.1);
    color: #d4af37;
  }

  /* v2.18e — branching sections */
  .se-section { margin-top: 18px; padding-top: 14px; border-top: 1px solid rgba(255,255,255,0.06); }
  .se-section h3 { font-family: 'JetBrains Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 1.4px; color: #9aa0a6; margin: 0 0 10px; font-weight: 500; }
  .se-section-hint { color: #6a6a6e; font-weight: 400; text-transform: none; letter-spacing: 0.4px; }

  .se-child-row, .se-process-row, .se-next-row { display: flex; align-items: center; gap: 8px; padding: 6px 8px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; margin-bottom: 4px; }
  .se-child-name { flex: 1; background: none; border: none; color: #f0efe9; text-align: left; font-size: 13px; cursor: pointer; padding: 0; }
  .se-child-name:hover { color: #d4af37; }

  .se-process-controls { display: flex; flex-direction: column; gap: 1px; }
  .se-process-controls button { background: rgba(255,255,255,0.04); color: #b8b8bc; border: none; padding: 1px 5px; border-radius: 3px; cursor: pointer; font-size: 9px; }
  .se-process-controls button:disabled { opacity: 0.3; cursor: not-allowed; }
  .se-process-label { flex: 1; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.08); color: #f4f4f6; padding: 6px 8px; border-radius: 6px; font-size: 12px; }
  .se-process-actions { display: flex; gap: 8px; align-items: center; margin-top: 6px; }
  .se-save-processes { background: rgba(212,175,55,0.15); color: #d4af37; border: 1px solid rgba(212,175,55,0.3); padding: 5px 12px; border-radius: 6px; cursor: pointer; font-size: 11px; }

  .se-next-arrow { color: #d4af37; font-weight: 600; }
  .se-next-kind { font-family: 'JetBrains Mono', monospace; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #9aa0a6; }
  .se-next-name { flex: 1; color: #f0efe9; font-size: 13px; }

  .se-add-row { background: rgba(212,175,55,0.08); color: #d4af37; border: 1px dashed rgba(212,175,55,0.3); padding: 7px 12px; border-radius: 6px; cursor: pointer; font-size: 11px; width: 100%; }
  .se-add-row:hover:not(:disabled) { background: rgba(212,175,55,0.15); border-style: solid; }
  .se-add-row:disabled { opacity: 0.5; cursor: not-allowed; }

  .se-add-next { display: flex; gap: 6px; align-items: center; padding: 8px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; }
  .se-add-next select { flex: 1; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); color: #f4f4f6; padding: 6px 8px; border-radius: 6px; font-size: 12px; }
  .se-confirm-next { background: linear-gradient(180deg, #d4af37, #b8941f); color: #0a0a0c; border: none; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 11px; }
  .se-cancel-next { background: rgba(255,255,255,0.05); color: #d8d8dc; border: 1px solid rgba(255,255,255,0.1); padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 11px; }

  .se-empty-row { padding: 8px 12px; color: #6a6a6e; font-size: 11px; font-style: italic; }
  .se-row-discard { background: rgba(255,255,255,0.04); color: #9aa0a6; border: 1px solid rgba(255,255,255,0.06); width: 22px; height: 22px; border-radius: 50%; cursor: pointer; font-size: 11px; line-height: 1; padding: 0; }
  .se-row-discard:hover { background: rgba(220,100,100,0.15); color: #d48484; border-color: rgba(220,100,100,0.3); }
</style>
